import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, User, ArrowRight, CheckCircle2, Sparkles, Send, Youtube, Instagram, Search, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';

interface WishlistSectionProps {
  selectedPlan?: string;
}

const WishlistSection: React.FC<WishlistSectionProps> = ({ selectedPlan = 'general' }) => {
  const [step, setStep] = useState<'form' | 'beta-ask' | 'discount-offer' | 'survey' | 'success'>('form');
  const [surveyStep, setSurveyStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [surveyData, setSurveyData] = useState<any>({
    preferredPlan: selectedPlan,
    referral: '',
    description: '',
    platforms: [],
    followers: ''
  });

  // Track origin
  const [origin, setOrigin] = useState('direct');

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get('utm_source');
    
    if (utmSource) {
      setOrigin(utmSource.toLowerCase());
    } else if (document.referrer) {
      const ref = document.referrer.toLowerCase();
      if (ref.includes('instagram')) setOrigin('instagram');
      else if (ref.includes('youtube')) setOrigin('youtube');
      else if (ref.includes('tiktok')) setOrigin('tiktok');
      else if (ref.includes('google')) setOrigin('google');
      else {
        try {
          setOrigin(new URL(document.referrer).hostname);
        } catch(e) {
          setOrigin('unknown');
        }
      }
    }
  }, []);

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const updateProgress = async (currentData: any) => {
    try {
      await apiFetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          plan: selectedPlan,
          is_beta_tester: currentData.is_beta_tester ?? isBetaTester,
          survey_data: currentData.survey_data ?? surveyData
        })
      });
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          plan: selectedPlan,
          origin,
          device: window.innerWidth < 768 ? 'mobile' : 'desktop'
        })
      });
      
      setStep('beta-ask');
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleBetaChoice = async (choice: boolean) => {
    setIsBetaTester(choice);
    setLoading(true);
    
    // Save the choice immediately
    await updateProgress({ is_beta_tester: choice });
    setLoading(false);

    if (choice) {
      setStep('survey');
    } else {
      setStep('discount-offer');
    }
  };

  const handleSurveyOption = async (field: string, value: any) => {
    const newData = { ...surveyData, [field]: value };
    setSurveyData(newData);
    
    // Save progress after each answer
    await updateProgress({ survey_data: newData });

    // Move to next step or complete
    const totalSteps = shouldAskReferral ? 4 : 3;
    if (surveyStep < totalSteps - 1) {
      setSurveyStep(prev => prev + 1);
    } else {
      setStep('success');
      toast.success('Information saved! We will be in touch.');
    }
  };

  const shouldAskReferral = origin === 'direct' || origin === 'unknown' || origin.includes('google');

  const surveyQuestions = [
    ...(shouldAskReferral ? [{
      id: 'referral',
      label: 'How did you hear about us?',
      options: ['YouTube', 'Instagram', 'TikTok', 'Google', 'Twitter', 'Other'],
      field: 'referral'
    }] : []),
    {
      id: 'description',
      label: 'What best describes you?',
      options: ['Just Starting', 'Content Creator', 'Video Editor', 'Brand/Business', 'Freelancer', 'Other'],
      field: 'description'
    },
    {
      id: 'platforms',
      label: 'Which is your primary platform?',
      options: ['YouTube', 'Instagram', 'TikTok', 'Twitter', 'Other'],
      field: 'platforms',
      isMulti: false // Switching to single for smoother one-by-one flow as requested
    },
    {
      id: 'followers',
      label: 'Follower count across platforms?',
      options: ['Just Starting', '1K+', '10K+', '100K+', '1M+'],
      field: 'followers'
    }
  ];

  return (
    <section id="wishlist-section" className="py-24 relative overflow-hidden bg-background">
      <div className="absolute inset-0 z-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(var(--color-primary)_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-bold font-display text-foreground mb-4 uppercase tracking-tighter">
            Join the <span className="text-primary">Wishlist</span>
          </h2>
          <p className="text-muted-foreground text-base max-w-lg mx-auto font-sans tracking-tight">
            We're currently in private beta. Join the wishlist to get early access and an exclusive launch discount.
          </p>
        </div>

        <Card className="glass-panel border-none shadow-2xl shadow-primary/5 rounded-[2rem] overflow-hidden">
          <CardContent className="p-8 md:p-12">
            <AnimatePresence mode="wait">
              {step === 'form' && (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <form onSubmit={handleInitialSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Your Name (Optional)</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                          <Input 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Doe" 
                            className="pl-12 h-14 bg-muted/30 border-border rounded-2xl focus-visible:ring-primary font-sans"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Email address (Required)</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                          <Input 
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="john@example.com" 
                            className="pl-12 h-14 bg-muted/30 border-border rounded-2xl focus-visible:ring-primary font-sans"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full h-16 rounded-2xl bg-primary text-primary-foreground hover:opacity-90 font-bold uppercase tracking-[0.2em] text-xs transition-all shadow-xl shadow-primary/20"
                    >
                      {loading ? 'Joining...' : 'Wishlist Now'} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>

                    <div className="flex items-center gap-2 justify-center text-[10px] text-muted-foreground font-medium uppercase tracking-widest">
                      <Sparkles className="w-3 h-3 text-primary" />
                      <span>Priority Access for early supporters</span>
                    </div>
                  </form>
                </motion.div>
              )}

              {step === 'beta-ask' && (
                <motion.div
                  key="beta-ask"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  className="text-center space-y-8 py-4"
                >
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-display uppercase tracking-tight mb-2">Interested in beta testing?</h3>
                    <p className="text-muted-foreground text-sm font-sans max-w-sm mx-auto">Get early access and help us build the most powerful rhythmic physics engine.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      onClick={() => handleBetaChoice(true)}
                      className="h-14 px-10 rounded-2xl bg-primary hover:opacity-90 text-primary-foreground font-bold uppercase tracking-widest text-xs"
                    >
                      Yes, sign me up!
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleBetaChoice(false)}
                      className="h-14 px-10 rounded-2xl border-border text-foreground font-bold uppercase tracking-widest text-xs hover:bg-muted/50"
                    >
                      No, just the discount
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 'discount-offer' && (
                <motion.div
                  key="discount-offer"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  className="text-center space-y-8 py-4"
                >
                  <div className="relative inline-block">
                    <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(var(--color-primary),0.3)] animate-pulse">
                      <span className="text-2xl font-black text-primary-foreground">40%</span>
                    </div>
                    <Badge className="absolute -top-2 -right-4 bg-yellow-400 text-black border-none font-black animate-bounce">OFF</Badge>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold font-display uppercase tracking-tight mb-2">Exclusive Launch Discount</h3>
                    <p className="text-muted-foreground text-sm font-sans max-w-sm mx-auto">Complete our quick survey to lock in your 40% discount for launch day.</p>
                  </div>
                  <Button 
                    onClick={() => setStep('survey')}
                    className="w-full max-w-sm h-16 rounded-2xl bg-cyan-600 text-white hover:bg-cyan-700 shadow-xl shadow-cyan-600/20 transition-all duration-300 font-black uppercase tracking-[0.2em] text-sm"
                  >
                    Claim 40% Discount <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              )}

              {step === 'survey' && (
                <motion.div
                  key={`survey-${surveyStep}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center">
                    <Badge className="bg-primary/10 text-primary border-primary/20 mb-4 px-4 py-1 uppercase tracking-widest text-[9px]">
                      {isBetaTester ? 'Beta Application' : 'Discount Unlock'}
                    </Badge>
                    <h3 className="text-2xl font-bold font-display uppercase tracking-tight">
                      {isBetaTester ? 'Apply for Beta Tester' : 'Unlock Your Discount'}
                    </h3>
                    <p className="text-muted-foreground text-xs mt-2 font-sans tracking-wide">
                      {isBetaTester 
                        ? 'Just a few quick questions to help us process your application' 
                        : 'Answer a couple of questions to help us prepare for launch'}
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center px-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                          {surveyQuestions[surveyStep].label}
                        </p>
                        <span className="text-[10px] font-black text-primary/50">
                          {surveyStep + 1} / {surveyQuestions.length}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {surveyQuestions[surveyStep].options.map(opt => (
                          <button 
                            key={opt}
                            disabled={loading}
                            onClick={() => handleSurveyOption(surveyQuestions[surveyStep].field, opt)}
                            className={`p-5 rounded-2xl border text-xs font-bold uppercase tracking-widest transition-all text-left flex items-center justify-between group ${
                              surveyData[surveyQuestions[surveyStep].field] === opt 
                                ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20 scale-[1.02]' 
                                : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/50'
                            }`}
                          >
                            {opt}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                               surveyData[surveyQuestions[surveyStep].field] === opt 
                               ? 'bg-primary-foreground border-primary-foreground' 
                               : 'border-muted-foreground/30 group-hover:border-primary/50'
                            }`}>
                              {surveyData[surveyQuestions[surveyStep].field] === opt && <div className="w-2 h-2 bg-primary rounded-full" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="w-full bg-muted/30 h-1.5 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${((surveyStep + 1) / surveyQuestions.length) * 100}%` }}
                        className="h-full bg-primary"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12 space-y-6"
                >
                  <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                    <CheckCircle2 className="w-12 h-12 text-primary" />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-4xl font-bold font-display uppercase tracking-tighter">You're on the list!</h3>
                    <p className="text-muted-foreground text-sm font-sans tracking-tight max-w-sm mx-auto leading-relaxed">
                      {isBetaTester 
                        ? "Thank you for applying! We're reviewing beta applications and will reach out to selected testers soon."
                        : "Thank you! We've saved your interest and will send your 40% discount code to your email as soon as we launch."}
                    </p>
                  </div>
                  <div className="flex justify-center gap-4 pt-8">
                     <Youtube className="w-5 h-5 text-muted-foreground/30 hover:text-red-500 cursor-pointer transition-colors" />
                     <Instagram className="w-5 h-5 text-muted-foreground/30 hover:text-pink-500 cursor-pointer transition-colors" />
                     <Search className="w-5 h-5 text-muted-foreground/30 hover:text-primary cursor-pointer transition-colors" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default WishlistSection;
